import React, { useCallback, useEffect, useRef, useState } from "react";
import { MoveHorizontal } from "lucide-react";

// Draggable before/after image comparison slider
export default function BeforeAfter({ before, after, beforeLabel = "Before", afterLabel = "After" }) {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50);
  const draggingRef = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      setFromClientX(clientX);
    };
    const onUp = () => { draggingRef.current = false; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
  }, [setFromClientX]);

  const startDrag = (e) => {
    draggingRef.current = true;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    setFromClientX(clientX);
  };

  return (
    <div
      ref={containerRef}
      onMouseDown={startDrag}
      onTouchStart={startDrag}
      className="relative w-full h-[320px] md:h-[480px] rounded-3xl overflow-hidden select-none cursor-ew-resize shadow-2xl"
    >
      {/* After (full) */}
      <img src={after} alt="After" draggable={false} className="absolute inset-0 w-full h-full object-cover" />
      <span className="absolute top-4 right-4 z-20 bg-homi-gold text-homi-black text-xs font-heading font-bold px-3 py-1.5 rounded-full">{afterLabel}</span>

      {/* Before (clipped) */}
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img src={before} alt="Before" draggable={false} className="absolute inset-0 h-full object-cover" style={{ width: containerRef.current ? containerRef.current.offsetWidth : "100%", maxWidth: "none" }} />
        <span className="absolute top-4 left-4 z-20 bg-homi-black/80 text-white text-xs font-heading font-bold px-3 py-1.5 rounded-full">{beforeLabel}</span>
      </div>

      {/* Handle */}
      <div className="absolute top-0 bottom-0 z-30 pointer-events-none" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
        <div className="w-1 h-full bg-white/90 mx-auto" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center">
          <MoveHorizontal className="w-5 h-5 text-homi-black" />
        </div>
      </div>
    </div>
  );
}
